<template>
  <div>
    <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
      {{ snackText }}
      <v-btn text @click="snack = false">Close</v-btn>
    </v-snackbar>

    <div class="text-center" v-if="webcam && !form">
      <video
        id="webcam-preview"
        style="width:100%;height:auto;max-width:400px;"
      ></video>
      <br />
      <br />
    </div>

    <v-dialog v-model="dialog" persistent max-width="390">
      <v-card>
        <v-card-title class="text-h5">
          {{ selectedEmployee.barcodeId }}
        </v-card-title>
        <v-card-text>
          Punch {{ direction
          }}<span v-if="selectedEmployee.firstName"
            >&nbsp;{{ selectedEmployee.firstName }}
            {{ selectedEmployee.lastName }}</span
          >?
          <span v-if="sameDirectionWarning" class="red--text"
            >{{ selectedEmployee.firstName }}'s last punch on this jobsite was a
            punch {{ direction }} at {{ sameDirectionWarningTimestamp }}.</span
          >
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="green darken-1"
            text
            @click="addRecord('camera')"
            :disabled="disableButton"
          >
            Yes
          </v-btn>
          <v-btn color="red darken-1" text @click="dialog = false">
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div v-if="form" style="text-align: center;">
      <h1 class="title">Manual Punch</h1>
      <v-form ref="form">
        <v-autocomplete
          placeholder="Employee Search"
          :items="allEmployees"
          return-object
          item-text="name"
          v-model="selectedEmployee"
          hide-details
        >
        </v-autocomplete>
        <v-radio-group v-model="direction">
          <v-radio :key="1" label="Punch In" value="in"></v-radio>
          <v-radio :key="2" label="Punch Out" value="out"></v-radio>
        </v-radio-group>
        <v-btn
          color="secondary"
          block
          x-large
          @click="saveForm"
          :disabled="disableButton"
        >
          Save
        </v-btn>
        <br />
      </v-form>
    </div>

    <div v-if="!webcam && !form">
      <v-btn color="primary" block x-large @click="showWebcam('in')">
        <v-icon small>east</v-icon> &nbsp;Punch In
      </v-btn>
      <br />
      <div class="text-center">or</div>
      <br />
      <v-btn color="primary" block x-large @click="showWebcam('out')">
        <v-icon small>west</v-icon> &nbsp;Punch Out
      </v-btn>
      <br />
      <div class="text-center">or</div>
      <br />
      <v-btn color="secondary" block x-large @click="punchDashboard">
        Return to Dashboard
      </v-btn>
      <div
        v-if="
          user.permissionGroups &&
            user.permissionGroups.includes('allowManualPunch')
        "
      >
        <br />
        <br />
        <br />
        <div class="text-center">
          Having camera trouble?
          <v-btn rounded outlined text x-small class="ml-2" @click="form = true"
            >Click here</v-btn
          >
        </div>
      </div>
    </div>
    <v-btn color="error" block x-large v-if="webcam || form" @click="cancel()">
      <v-icon small>close</v-icon> &nbsp;Cancel
    </v-btn>
  </div>
</template>

<script>
import fb from "@/main";
import { mapState } from "vuex";
import firebase from "firebase/app";
import "firebase/installations";
import moment from "moment";

// The Payroll Clock-In login belongs on the yard iPads. A punch from it farther
// than this from the job site came from another device. Yard iPad punches land
// up to about 0.6 miles from their job site pin, mostly because the Yard Payroll
// pin is at Randall Ave while that iPad sits at the Oak Point yard, so this
// leaves room for that.
const KIOSK_MAX_DISTANCE_METERS = 1609; // 1 mile

export default {
  data() {
    return {
      webcam: false,
      disableButton: false,
      form: false,
      uid: null,
      dialog: false,
      direction: "",
      snack: false,
      snackText: "",
      snackColor: null,
      selectedEmployee: {},
      sameDirectionWarning: false,
      sameDirectionWarningTimestamp: "",
    };
  },
  async created() {
    if (!this.employees.length) {
      await this.$store.dispatch("getEmployees", fb);
    }
  },
  computed: {
    ...mapState(["user", "employees"]),
    allEmployees: function() {
      return this.employees.map((e) => {
        return {
          name: `${e.firstName} ${e.lastName} (${e.barcodeId})`,
          ...e,
        };
      });
    },
  },
  mounted: async function() {},
  methods: {
    punchDashboard: function() {
      this.$router.push("/punch-dashboard");
    },
    cancel: function() {
      this.webcam = false;
      this.form = false;
      this.killVideo();
      this.selectedEmployee = {};
      this.direction = "";
    },
    saveForm: async function() {
      const employee = this.selectedEmployee;
      if (!employee || !employee.docId || !this.direction) {
        return;
      }
      this.addRecord("manual");
    },
    killVideo() {
      const videoElement = document.getElementById("webcam-preview");
      if (videoElement) {
        // kill camera and hide
        videoElement.srcObject
          .getVideoTracks()
          .forEach((track) => track.stop());
        this.webcam = false;
      }
    },
    doesShiftOverlap(scheduledShifts) {
      const daysOfWeek = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      let earlyAllowance = 7; // Early clock-in allowance in minutes
      //update early allowance if foreman to 37 min
      if (
        this.selectedEmployee &&
        this.selectedEmployee.department === "foreman"
      ) {
        earlyAllowance = 37;
      }

      // Helper function to convert time to minutes from midnight
      const timeToMinutes = (time) => {
        const [hours, minutes] = time.split(":").map(Number);
        return hours * 60 + minutes;
      };

      // Get the current time and day information
      const now = new Date();
      const currentTimeMinutes = timeToMinutes(
        now
          .getHours()
          .toString()
          .padStart(2, "0") +
          ":" +
          now
            .getMinutes()
            .toString()
            .padStart(2, "0")
      );
      const currentDayName = daysOfWeek[now.getDay()];
      const nextDayName = daysOfWeek[(now.getDay() + 1) % 7]; // For checking against shifts that span midnight

      // Determine if current time is within the shift window
      const isWithinShiftWindow = (startTime, endTime, isTodayShift) => {
        if (startTime > endTime) {
          // Shift spans to the next day
          if (isTodayShift) {
            return (
              currentTimeMinutes >= startTime - earlyAllowance ||
              currentTimeMinutes <= endTime
            ); // Early clock-in for today's shift or still within shift that spans past midnight
          } else {
            return currentTimeMinutes <= endTime; // Early hours of the next day, within the end of a spanning shift
          }
        } else {
          // Shift within the same day
          return (
            currentTimeMinutes >= startTime - earlyAllowance &&
            currentTimeMinutes <= endTime
          );
        }
      };

      for (const shift of scheduledShifts) {
        const shiftStartTime = timeToMinutes(shift.startTime);
        const shiftEndTime = timeToMinutes(shift.endTime);
        const isTodayShift = shift.daysOfWeek.includes(currentDayName);
        const isTomorrowShift =
          shift.daysOfWeek.includes(nextDayName) &&
          shiftStartTime > shiftEndTime;

        // Check if the current day matches the shift day, or if we're in the early hours of the next day for a spanning shift
        if (
          isWithinShiftWindow(shiftStartTime, shiftEndTime, isTodayShift) ||
          (isTomorrowShift && currentTimeMinutes <= shiftEndTime)
        ) {
          return true; // Overlap found: Current time is within or close to a scheduled shift
        }
      }
      return false; // No overlap found
    },
    getGeolocation: function() {
      return new Promise((resolve) => {
        if (!navigator.geolocation) {
          resolve({ error: "not_supported" });
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              success: true,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: position.timestamp,
            });
          },
          (error) => {
            // Return error type so we can provide specific feedback
            let errorType = "unknown";
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorType = "denied";
                break;
              case error.POSITION_UNAVAILABLE:
                errorType = "unavailable";
                break;
              case error.TIMEOUT:
                errorType = "timeout";
                break;
            }
            console.warn("Geolocation error:", error.message);
            resolve({ error: errorType, message: error.message });
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          }
        );
      });
    },
    // Why a Payroll Clock-In punch can't be accepted, or null if it can
    getKioskLocationProblem: function(geolocation, jobSite, direction) {
      if (!geolocation || !geolocation.success) {
        return `Location is required to punch ${direction}. Please try again.`;
      }
      if (!jobSite.geolocation) {
        return null;
      }
      const distance = this.calculateDistance(
        geolocation.latitude,
        geolocation.longitude,
        jobSite.geolocation.latitude,
        jobSite.geolocation.longitude
      );
      if (distance > KIOSK_MAX_DISTANCE_METERS) {
        const miles = (distance / 1609).toFixed(1);
        return `Punches with this login must be made on site. This device is ${miles} miles away.`;
      }
      return null;
    },
    calculateDistance: function(lat1, lon1, lat2, lon2) {
      // Calculate distance using Haversine formula
      const R = 6371e3; // Earth's radius in meters
      const φ1 = (lat1 * Math.PI) / 180;
      const φ2 = (lat2 * Math.PI) / 180;
      const Δφ = ((lat2 - lat1) * Math.PI) / 180;
      const Δλ = ((lon2 - lon1) * Math.PI) / 180;

      const a =
        Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

      return R * c; // Returns distance in meters
    },
    addRecord: async function(source) {
      this.disableButton = true;
      const direction = this.direction;
      const employee = this.selectedEmployee;
      const jobSiteId = this.$route.params.jobSite;
      // get jobsite data to eventually check if jobsite.jobId starts with "15-"
      const getJobSite = await fb.db
        .collection("jobSites")
        .doc(jobSiteId)
        .get();
      const jobSite = getJobSite.data();

      if (!employee) {
        alert("Please select an employee.");
        this.disableButton = false;
        return;
      }
      // driver and mechanic codes or anyone clocked in with prefix of 15-xxxx. Asbestos
      // and supervisor were dropped (Nov 2026) once those crews were scheduled. The
      // employee app's punch function carries the same list - keep in step.
      if (
        ["driver", "mechanic"].includes(employee.department) ||
        jobSite.jobId.startsWith("15-")
      ) {
        // nothign happens
      } else {
        if (direction === "in") {
          const getScheduledRecords = await fb.db
            .collection("employeeSchedule")
            .where("employees", "array-contains", employee.docId)
            .where("jobSite", "==", jobSiteId)
            .where("lastShiftDate", ">=", moment().format("YYYY-MM-DD"))
            .get();
          const scheduledRecords = getScheduledRecords.docs.map((doc) => {
            return { id: doc.id, ...doc.data() };
          });
          const punchInAllowed = this.doesShiftOverlap(scheduledRecords);
          if (!punchInAllowed) {
            alert(
              "Sorry, your punch in is not allowed because you are not scheduled for this time."
            );
            this.disableButton = false;
            return false;
          }
        } else if (direction === "out") {
          const everScheduled = await fb.db
            .collection("employeeSchedule")
            .where("employees", "array-contains", employee.docId)
            .limit(1)
            .get();
          if (everScheduled.empty) {
            alert(
              "Sorry, your punch out is not allowed because you have not been added to a schedule yet. Please contact the office."
            );
            this.disableButton = false;
            return false;
          }
        }
      }

      // Capture geolocation BEFORE proceeding with punch
      const geolocation = await this.getGeolocation();

      // Check if geolocation was DENIED by user - only block in this case
      if (geolocation && geolocation.error === "denied") {
        this.killVideo();
        this.dialog = false;
        this.form = false;
        this.disableButton = false;

        this.snack = true;
        this.snackText = `Geolocation is required to punch ${direction}. Please enable in browser settings and try again.`;
        this.snackColor = "error";
        this.selectedEmployee = {};
        this.direction = "";
        return;
      }

      if (this.user.role === "Payroll Clock-In") {
        const kioskProblem = this.getKioskLocationProblem(
          geolocation,
          jobSite,
          direction
        );
        if (kioskProblem) {
          this.killVideo();
          this.dialog = false;
          this.form = false;
          this.disableButton = false;

          this.snack = true;
          this.snackText = kioskProblem;
          this.snackColor = "error";
          this.selectedEmployee = {};
          this.direction = "";
          return;
        }
      }

      this.killVideo();
      this.dialog = false;
      this.form = false;

      // insert into firebase
      const punchData = {
        employee: employee.docId,
        direction,
        jobSite: this.$route.params.jobSite,
        time: firebase.firestore.FieldValue.serverTimestamp(),
        createdBy: this.user.uid,
        source,
      };

      // Add geolocation data if available
      if (geolocation && geolocation.success) {
        // Use Firebase GeoPoint for coordinates
        punchData.geolocation = new firebase.firestore.GeoPoint(
          geolocation.latitude,
          geolocation.longitude
        );
        punchData.geolocationAccuracy = geolocation.accuracy;
        punchData.geolocationCapturedAt = new Date(geolocation.timestamp);
      } else if (geolocation && geolocation.error) {
        // Store that location was attempted but failed (except "denied" which is blocked above)
        punchData.geolocationError = geolocation.error;
        punchData.geolocationErrorMessage = geolocation.message || "Location unavailable";
      }

      // Payroll Clock-In punches only: unique to this browser on this device,
      // so payroll can tell a yard iPad punch from one made on another device
      // (the employee app stores the same field)
      if (this.user.role === "Payroll Clock-In") {
        try {
          punchData.installationId = await firebase.installations().getId();
        } catch (e) {
          console.warn("Could not read installation ID:", e.message);
        }
      }

      await fb.db.collection("punchRecords").add(punchData);
      this.disableButton = false;

      // alert user of success of punch with location status
      this.snack = true;
      let locationWarning = "";
      if (geolocation && geolocation.error) {
        if (geolocation.error === "unavailable") {
          locationWarning = " (Location unavailable)";
        } else if (geolocation.error === "timeout") {
          locationWarning = " (Location timeout)";
        } else if (geolocation.error === "not_supported") {
          locationWarning = " (Location not supported)";
        }
      }
      this.snackText = `${employee.firstName} ${employee.lastName} is punched ${direction}.${locationWarning}`;
      this.snackColor = geolocation && geolocation.error ? "warning" : "success";

      this.selectedEmployee = {};
      this.direction = "";
    },
    showWebcam: async function(direction) {
      const hints = new Map();
      const formats = [ZXing.BarcodeFormat.QR_CODE];

      hints.set(ZXing.DecodeHintType.POSSIBLE_FORMATS, formats);

      const codeReader = new ZXing.BrowserMultiFormatReader(hints);

      this.webcam = true;
      this.selectedEmployee = {};
      codeReader.decodeFromVideoDevice(
        null,
        "webcam-preview",
        async (result, err, controls) => {
          const volkUrl = "https://app.volkindustrial.com/portal/employee/";
          const albaUrl = "https://app.albademo.com/portal/employee/";
          const scannedText = result ? result.text.trim() : "";
          const matchedUrl = scannedText.startsWith(volkUrl)
            ? volkUrl
            : scannedText.startsWith(albaUrl)
            ? albaUrl
            : null;
          if (result && scannedText && matchedUrl) {
            const barcodeId = scannedText.replace(matchedUrl, "") * 1;
            const employee = this.allEmployees.find(
              (e) => e.barcodeId == barcodeId
            );

            if (employee) {
              try {
                // get most recent punch
                const jobSite = this.$route.params.jobSite;
                const punRes = await fb.db
                  .collection("punchRecords")
                  .where("employee", "==", employee.docId)
                  .where("jobSite", "==", jobSite)
                  .orderBy("time", "desc")
                  .limit(1)
                  .get();
                const punches = [];
                punRes.forEach((p) => {
                  punches.push(p.data());
                });
                if (punches.length) {
                  const mostRecentPunch = punches[0];
                  this.sameDirectionWarning =
                    mostRecentPunch.createdBy === this.user.uid &&
                    mostRecentPunch.direction === direction;
                  this.sameDirectionWarningTimestamp = new Date(
                    mostRecentPunch.time.seconds * 1000
                  ).toLocaleString("en-US");
                }

                this.selectedEmployee = employee;
                this.dialog = true;
                this.direction = direction;
              } catch (e) {
                this.snack = true;
                this.snackText = `Error punching ID ${barcodeId}.`;
                this.snackColor = "error";
                console.log(e);
              }
            } else {
              this.snack = true;
              this.snackText = `No employee found for ID ${barcodeId}.`;
              this.snackColor = "error";
            }
            // controls.stop() // tells to stop scanning but doesn't kill video
          }

          if (err) {
            // As long as this error belongs into one of the following categories
            // the code reader is going to continue as excepted. Any other error
            // will stop the decoding loop.
            //
            // Excepted Exceptions:
            //
            //  - NotFoundException
            //  - ChecksumException
            //  - FormatException

            if (err instanceof ZXing.NotFoundException) {
              //console.log('No QR code found.')
            }

            if (err instanceof ZXing.ChecksumException) {
              //console.log('A code was found, but it\'s read value was not valid.')
            }

            if (err instanceof ZXing.FormatException) {
              //console.log('A code was found, but it was in a invalid format.')
            }
          }
        }
      );
    },
  },
};
</script>
